import * as React from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
} from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import DeleteIcon from "@mui/icons-material/Delete";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { API_BASE_URL } from "../api/config";
import { rawFetch } from "../api/httpClient";

const SEVERITY_COLORS = {
  HIGH: "#ffcdd2",
  MEDIUM: "#fff9c4",
  LOW: "#c8e6c9",
};

const getSeverityColor = (reason) => {
  const r = (reason || "").toUpperCase();
  if (r.includes("HARASS") || r.includes("DANGER") || r.includes("FRAUD") || r.includes("ABUS"))
    return SEVERITY_COLORS.HIGH;
  if (r.includes("SPAM") || r.includes("INAPP") || r.includes("INAPPROPRI"))
    return SEVERITY_COLORS.MEDIUM;
  return SEVERITY_COLORS.LOW;
};

export const ReportsList = () => {
  const [practitionerReports, setPractitionerReports] = React.useState([]);
  const [echoReports, setEchoReports] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [tab, setTab] = React.useState(0);
  const [confirmDialog, setConfirmDialog] = React.useState(null);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: "" });

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const urls = [
          `${API_BASE_URL}/reports/practitioners`,
          // EchoController : GET /echos/reports/echos (admin)
          `${API_BASE_URL}/echos/reports/echos`,
        ];
        const [r1, r2] = await Promise.all(
          urls.map((u) =>
            rawFetch(u, { headers: { "X-React-Admin": "true" } }).then((res) => {
              if (res.status === 404 || res.status === 501) return [];
              return res.ok ? res.json() : [];
            })
          )
        );
        const list1 = Array.isArray(r1) ? r1 : r1?.data ?? [];
        const list2 = Array.isArray(r2) ? r2 : r2?.data ?? [];
        if (!cancelled) {
          setPractitionerReports(list1);
          setEchoReports(list2);
        }
      } catch (_) {
        if (!cancelled) {
          setPractitionerReports([]);
          setEchoReports([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Signalement praticien (ReportedUserDTO) : « résolu » = DELETE /reports/{id}
  // (ReportedUserController, admin). Les signalements d'échos n'ont pas
  // d'endpoint de clôture : seule la suppression du contenu est proposée.
  const resolvePractitionerReport = async (reportId) => {
    try {
      const res = await rawFetch(`${API_BASE_URL}/reports/${encodeURIComponent(reportId)}`, {
        method: "DELETE",
        headers: { "X-React-Admin": "true" },
      });
      if (!res.ok && res.status !== 404) throw new Error(`HTTP ${res.status}`);
      setPractitionerReports((prev) => prev.filter((r) => r.id !== reportId));
      setSnackbar({ open: true, message: "Signalement résolu (supprimé)" });
    } catch (e) {
      setSnackbar({ open: true, message: `Erreur : ${e.message}` });
    }
  };

  const handleDeleteEcho = async (echoId, reportId) => {
    try {
      const res = await rawFetch(`${API_BASE_URL}/echos/${echoId}`, {
        method: "DELETE",
        headers: { "X-React-Admin": "true" },
      });
      if (!res.ok && res.status !== 404) throw new Error("Erreur suppression");
      setEchoReports((prev) => prev.filter((r) => r.id !== reportId));
      setSnackbar({ open: true, message: "Écho supprimé avec succès" });
    } catch (e) {
      setSnackbar({ open: true, message: `Erreur : ${e.message}` });
    }
    setConfirmDialog(null);
  };

  const handleDisablePractitioner = async (professionalId, reportId) => {
    try {
      const res = await rawFetch(`${API_BASE_URL}/users/status/${professionalId}/false`, {
        method: "PUT",
        headers: { "X-React-Admin": "true", "Content-Type": "application/json" },
      });
      if (!res.ok && res.status !== 404) throw new Error("Erreur désactivation");
      setSnackbar({ open: true, message: "Compte praticien désactivé" });
    } catch (e) {
      setSnackbar({ open: true, message: `Erreur : ${e.message}` });
    }
    setConfirmDialog(null);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <FlagIcon /> Signalements
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Signalements praticiens : GET /reports/practitioners (« Résolu » supprime le signalement).
        Échos signalés : GET /echos/reports/echos (pas de clôture côté API — supprimer le contenu si nécessaire).
      </Alert>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Signalements praticiens" />
        <Tab label="Signalements Échos" />
      </Tabs>
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : tab === 0 ? (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "primary.main", color: "white" }}>
                <TableCell>ID</TableCell>
                <TableCell>Praticien signalé</TableCell>
                <TableCell>Signalé par</TableCell>
                <TableCell>Raison</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {practitionerReports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    Aucun signalement praticien
                  </TableCell>
                </TableRow>
              ) : (
                practitionerReports.map((row, i) => {
                  const rid = row.id ?? `pr-${i}`;
                  return (
                    <TableRow
                      key={rid}
                      hover
                      sx={{ bgcolor: getSeverityColor(row.reason) }}
                    >
                      <TableCell>{row.id ?? "—"}</TableCell>
                      <TableCell>{row.reportedUserName || row.reportedUserId || "—"}</TableCell>
                      <TableCell>{row.reporterName || row.reporterId || "—"}</TableCell>
                      <TableCell>{row.reason ?? "—"}</TableCell>
                      <TableCell>{row.timestamp ? new Date(Number(row.timestamp)).toLocaleString("fr-FR") : "—"}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {row.reportedUserId && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<BlockIcon />}
                                onClick={() =>
                                  setConfirmDialog({
                                    type: "disablePro",
                                    professionalId: row.reportedUserId,
                                    reportId: rid,
                                  })
                                }
                              >
                                Désactiver
                              </Button>
                          )}
                          {row.id && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                startIcon={<CheckCircleIcon />}
                                onClick={() => resolvePractitionerReport(row.id)}
                              >
                                Résolu
                              </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "primary.main", color: "white" }}>
                <TableCell>ID écho</TableCell>
                <TableCell>Contenu</TableCell>
                <TableCell>Auteur</TableCell>
                <TableCell>Thème</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {echoReports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    Aucun écho signalé
                  </TableCell>
                </TableRow>
              ) : (
                echoReports.map((row, i) => {
                  // GET /echos/reports/echos renvoie des EchoDTO (id = id de l'écho).
                  const rid = row.id ?? `er-${i}`;
                  const echoId = row.echoId ?? row.id;
                  const content = String(row.content ?? "");
                  return (
                    <TableRow key={rid} hover>
                      <TableCell>{echoId ?? "—"}</TableCell>
                      <TableCell>{content ? (content.length > 120 ? `${content.slice(0, 120)}…` : content) : "—"}</TableCell>
                      <TableCell>{row.isAnonymous || row.anonymous ? "Anonyme" : (row.authorDisplayName || row.authorId || "—")}</TableCell>
                      <TableCell>{row.themeLabel || row.theme || "—"}</TableCell>
                      <TableCell>{row.createdAt ? new Date(row.createdAt).toLocaleString("fr-FR") : "—"}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {echoId && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() =>
                                  setConfirmDialog({
                                    type: "deleteEcho",
                                    echoId,
                                    reportId: rid,
                                  })
                                }
                              >
                                Supprimer le contenu
                              </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Confirmation dialog */}
      <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)}>
        <DialogTitle>Confirmer l'action</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog?.type === "deleteEcho"
              ? `Êtes-vous sûr de vouloir supprimer l'écho ${confirmDialog?.echoId} ? Cette action est irréversible.`
              : `Êtes-vous sûr de vouloir désactiver le compte du praticien ${confirmDialog?.professionalId} ?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>Annuler</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (confirmDialog?.type === "deleteEcho") {
                handleDeleteEcho(confirmDialog.echoId, confirmDialog.reportId);
              } else {
                handleDisablePractitioner(confirmDialog.professionalId, confirmDialog.reportId);
              }
            }}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        message={snackbar.message}
      />
    </Box>
  );
};
